import { UntypedFormControl, UntypedFormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import * as _ from 'lodash';
import {
  FormDefinition,
  FormFieldDefinition,
  LabelPosition,
  SelectOption,
  DependencyValue,
  FieldDependency,
  DependencyExpression
} from '@nuso/forms/def-types';
import {
  Form,
  FormField,
  FormFieldMap,
} from './form.model';

import {
  validateMetaswitchPassword,
  validateMetaswitchPIN,
  validateMetaswitchVoicemailPIN,
  validateIPAddress,
  validateUrl,
  validateUrlTrailing,
  validRange,
  validateMetaswitchExtension,
  validateMetaswitchExtensionNotRequired,
  validateMACAddress,
  validateAlphaNumericWithSpaces,
  validateEmailAddresses,
  validateEmails,
  zipCode,
  cvv,
  creditCard,
  validSelection,
  validSelectionNotRequired,
  validateSubdomain,
  timeRange,
  validateUSPhoneNumber,
  validateCheckSelectRequired,
  validateInternationalOrLocalPhoneNumberWithCountry,
  validateInternationalAndUSPhoneNumber,
  validateInternationalAndUSPhoneNumberLenient,
  validateE164WithoutPlus,
  allOrNothing,
  notBlank,
} from './validators';
import { addressSimple, letterRange, time12hour, bandwidthPortTime, brightlinkPortTime } from './validators';

import {
  min,
  max,
  range,
  digits,
  number,
  email,
  date,
  minDate,
  maxDate,
  dateISO,
  //creditCard,
  json,
  base64,
  uuid,
  equal,
  equalTo,
  greaterThanOrEqualTo,
  lessThanOrEqualTo,
  greaterThanField,
  lessThanField,
} from './validators-new';
import { checkSelectValues } from '@nuso/forms/util';
import { nullIfEmpty } from '@nuso/common/util-string';
import { isNotNullOrUndefined } from '@nuso/common/util-ts';
import { titleCase } from '@nuso/common/util-string';
import { PropertyFieldDefinitionInput } from '@nuso/common/types';
export class Forminator {

  public static createForm(def: FormDefinition): Form {
    const fieldList = this.normalizeFormFields(def.fields, def.labels || 'top');

    // Create an object where the keys are the 'name' properties and the values are the corresponding field objects
    const fields = fieldList.reduce((acc, field) => {
      acc[field.name] = field;
      return acc;
    }, {} as FormFieldMap);

    // Map the fields object to a new object containing only the 'control' properties
    // and filter out any null or undefined controls
    const controls: { [name: string]: AbstractControl } = Object.keys(fields).reduce((acc, key) => {
      const control = fields[key].control;
      if (control) {
        acc[key] = control;
      }
      return acc;
    }, {} as { [name: string]: AbstractControl });


    const group = new UntypedFormGroup(controls);

    this.setValidatorsOnControls(fields, controls);
    const form = {
      name: def.name,
      fields: fields,
      fieldList: fieldList,
      group: group,
      submitted: false
    };

    this.setFormOnAllFields(fields, form);

    return form;
  }

  public static propertyFieldsFromFormDef(def: FormDefinition): PropertyFieldDefinitionInput[] {
    const fieldList = this.normalizeFormFields(def.fields, def.labels || 'top');
    return fieldList
      .filter(f => ['hidden', 'row', 'group'].indexOf(f.type) === -1)
      .map((f: FormField) => ({
        prop: f.name,
        name: f.label,
        // Todo: perhaps do some math on the type
      }));
  }

  public static getLeafFields(fields: FormFieldDefinition[]): FormFieldDefinition[] {
    const result: FormFieldDefinition[] = [];

    for (const field of fields) {
      if (field.fields && Array.isArray(field.fields)) {
        result.push(...this.getLeafFields(field.fields));
      } else {
        result.push(field);
      }
    }

    return result;
  }

  public static getFlattenedValue(form: Form) {
    const fields = form.fields
    const data = form.group.value;

    const values: Record<string, any> = {};
    Object.keys(fields).forEach(key => {
      const field: FormField = fields[key];
      if (field.control) {
        const value = this.getValueForField(field, data);
        if (field.type === 'row') {
          // This value is nested inside of field.name, let's get the internal
          // object out, and put each key at the top level
          Object.keys(value).forEach(subKey => {
            values[subKey] = value[subKey];
          })
        } else {
          values[key] = value;
        }
      }
    });
    return values;
  }

  public static transformedDisplayValuesFromFormDef(def: FormDefinition, input: any): any {
    const fieldList = this.normalizeFormFields(def.fields, def.labels || 'top');

    return Object.keys(input || {})
      .reduce((acc, key) => {
        const field = fieldList.find(f => f.name === key);
        if (field) {
          acc[field.name] = this.getDisplayValueForField(field, input[key]);
        }
        return acc;
      }, {} as Record<string, any>);
  }


  public static dependenciesMet(field: FormField, fieldDependencies: FieldDependency[], _visiting?: Set<string>): boolean {
    if (!fieldDependencies || (fieldDependencies.length === 0)) {
      return true;
    }

    const visiting = _visiting || new Set<string>();
    if (visiting.has(field.name)) {
      return false;
    }
    visiting.add(field.name);

    const dependenciesMet = fieldDependencies.find(d => {
      const control = field.control.parent?.get(d.name);
      if (!control) { console.log('FAILED TO FIND DEPENDENCY', d.name); return false; }

      const depField = field.parentField ? field.parentField.fields?.[d.name] : field.form?.fields[d.name]
      if (!depField) { console.log('FAILED TO FIND DEPENDENCY FIELD', d.name); return false; }

      const dependencies = depField.showIfDependencies || depField.enableIfDependencies || [];
      if (d.requireVisible && !Forminator.dependenciesMet(depField, dependencies, visiting)) {
        return false;
      }

      switch (d.operator) {
        case 'equalTo':
          return control.value === d.value;

        case 'notEqualTo':
          return control.value !== d.value;

        case 'anyOf':
          return !!(<string[]>d.value).find(v => control.value === v);

        case 'contains':
          return control.value? control.value?.indexOf(<string>d.value) !== -1 : false;

        case 'greaterThan':
          return control.value && +control.value > +d.value;

        case 'lessThan':
          return control.value && +control.value < +d.value;

        case 'greaterThanOrEqual':
          return control.value && +control.value >= +d.value;

        case 'lessThanOrEqual':
          return control.value && +control.value <= +d.value;

        case 'checkSelected': {
          const values = this.checkSelectValues(control.value);
          return values.indexOf(d.value) !== -1;
        }

        case 'isValid': {
          return control.valid === d.value;
        }

        case 'hasValueAndIsValid': {
          if (!control.value || typeof control.value !== 'object') return false;
          return control.valid === d.value && Object.values(control.value).some(value => value != null && value !== '');
        }

        default:
          console.log('UNKNOWN showIf operator:', d.operator, {d});
          return false;
      }
    });

    return !!dependenciesMet;
  }

  public static dependencyControls(field: FormField, fieldDependencies: FieldDependency[]): AbstractControl[] {
    return (fieldDependencies || [])
      .map(d => field.control.parent?.get(d.name))
      .filter(isNotNullOrUndefined);
  }

  public static setValues(form: Form, input: any, options?: { onlySelf?: boolean; emitEvent?: boolean; }) {
    const values = this.inputValues(form.fields, input);
    form.group.setValue(values, options);
    this.disableFieldsThatAreHardcodedToDisabled(form.fieldList);
  }

  private static disableFieldsThatAreHardcodedToDisabled(fieldList: FormField[]) {
    for (const field of fieldList) {
      if (field.attrs.disabled) {
        field.control.disable();
      }

      if (field.type === 'group' || field.type === 'row') {
        this.disableFieldsThatAreHardcodedToDisabled(field.fieldList || []);
      }
    }
  }

  public static getTypedFieldValue(valueType: string, value: any) {
    switch(valueType) {
      case 'number':
        return (value === '' || value === null || value === undefined) ? null : +value;
      default:
        return value;
    }
  }

  public static getTypedValues(form: Form): any {
    const origValues = form.group.value;
    return _.keys(origValues).reduce((acc, key) => {
      const field = form.fields[key];
      const valueType = field.valueType || field.type;
      acc[key] = this.getTypedFieldValue(valueType, origValues[key]);
      return acc;
    }, {} as Record<string, any>);
  }

  public static getMutableValues(form: Form): any {
    return _.pickBy(form.group.value, (val, key) => {
      const field = form.fields[key];
      return !(field.type === 'readonly' || field.type === 'hidden');
    })
  }

  public static defaultValues(fields: FormFieldMap) {
    const defaults: Record<string, any> = {};
    Object.keys(fields).forEach(key => {
      const field: FormField = fields[key];
      if (field.control) {
        defaults[key] = _.isEmpty(field.fields) ? field.defaultTo ?? null : this.defaultValues(field.fields || {});
      }
    });
    return defaults;
  }

  public static inputValues(fields: FormFieldMap, input: any, rootPath?: string) {
    const values: Record<string, any> = {};
    Object.keys(fields).forEach(key => {
      const field: FormField = fields[key];
      if (field.control) {
        const value = this.getValueForField(field, input, rootPath);
        values[key] = value;
      }
    });
    return values;
  }

  public static getValueForField(field: FormField, input: any, rootPath?: string) {
    const key = field.name;
    const path = rootPath ? [rootPath, key].join('.') : key;

    if ( field.type === 'checkselect') {
      return this.arrayToCheckSelectValue(field, _.get(input, path, []))
    }

    if (field.type === 'group' || field.type === 'row' ) {
      return this.inputValues(field.fields || {}, _.get(input, path));
    }

    if (field.type === 'daterange') {
      return {
        fromDate: _.get(input, [path, 'fromDate'], null),
        toDate: _.get(input, [path, 'toDate'], null)
      }
    }

    return _.get(input, path, field.defaultTo ?? null)
  }

  public static getDisplayValueForField(field: FormField, value: any) {
    if ( ['select', 'radio' ].includes(field.type) ) {
      return field.options?.find(o => o.value === value)?.label;
    }

    return value;
  }

  // convert { a: true, b: false, c: true } => ['a', 'c']
  public static checkSelectValues(obj: any): any[] {
    return checkSelectValues(obj);
  }

  // values => [{ }]
  public static arrayToCheckSelectValue(field: FormField, values: any[]) {
    const keys = _.map(field.fields, 'name');
    return keys.reduce((obj, key) => {
      obj[key] = values.indexOf(key) !== -1;
      return obj;
    }, {} as Record<string, boolean>)
  }

  public static nullEmptyFields(data: any) {
    return _.transform<any, any>(data, (result, value, key) => {
      result[key] = nullIfEmpty(value);
    }, {})
  }

  public static toValueLabel(values: string[]) {
    return values.map(value => ({
      value,
      label: value
    }))
  }

  private static setFormOnAllFields(fields: FormFieldMap, form: Form) {
    Object.keys(fields).forEach(key => {
      const field: FormField = fields[key];
      field.form = form;
      if (field.fields ) {
        this.setFormOnAllFields(field.fields, form);
      }
    });
  }

  private static setValidatorsOnControls(fields: FormFieldMap, controls: { [name: string]: AbstractControl }) {
    Object.keys(controls).forEach((key) => {
      const control: AbstractControl = controls[key];
      const field: FormField = fields[key];
      if (field.type === 'checkselect') {
        control.setValidators(this.getAngularValidators(field, controls));
      } else if ( (<UntypedFormGroup>control).controls && field.fields ) {
        control.setValidators(this.getAngularValidators(field, controls));
        this.setValidatorsOnControls(field.fields, (<UntypedFormGroup>control).controls);
      } else {
        control.setValidators(this.getAngularValidators(field, controls));
      }
    });
  }

  public static setValidatorsOnField(field: FormField, validators: any, controls: { [name: string]: AbstractControl }) {
    field.validators = validators;
    field.control.setValidators(this.getAngularValidators(field, controls));
  }

  private static normalizeFormFields(formFields: FormFieldDefinition[], labelPosition: LabelPosition): FormField[] {
    return formFields.map(f => this.normalizeFormField(f, labelPosition));
  }

  private static normalizeFormField(f: FormFieldDefinition, labelPosition: LabelPosition): FormField {
    const subfieldList: FormField[] = this.createSubFields(f, labelPosition);
    const subfields = _.keyBy(subfieldList, 'name');
    const subcontrols = _.mapValues(subfields, (s) => s.control);

    const type = f.type || 'text';
    const isNonFormControl = ['divider', 'icon-button'].includes(type || 'text');
    const control = (
      !_.isEmpty(subcontrols) ? new UntypedFormGroup(subcontrols) : (isNonFormControl ? null : new UntypedFormControl())
    ) as AbstractControl;

    const field: FormField = {
      name: f.name,
      label: f.label || titleCase(f.name),
      label2: f.label2,
      labelPosition: labelPosition,
      floatLabel: f.floatLabel,
      type,
      placeholder: f.placeholder,
      help: f.help,
      prefixIcon: f.prefixIcon,
      actionButton: f.actionButton,
      validators: f.validators,
      options: f.options && this.normalizeSelectOptions(f.options),
      showIfDependencies: f.showIf && this.normalizeDependencyExpressions(f.showIf),
      enableIfDependencies: f.enableIf && this.normalizeDependencyExpressions(f.enableIf),
      resetOnDisable: f.resetOnDisable,
      fieldList: subfieldList,
      fields: subfields,
      control,
      defaultTo: f.defaultTo,
      attrs: f.attrs || {},
      valueType: f.valueType,
      filter: f.filter
    };

    // Hold onto the parent so we can look at the enable/showIf dependencies in the validators
    subfieldList.forEach(f => f.parentField = field);

    return field;
  }

  private static createSubFields(f: FormFieldDefinition, labelPosition: LabelPosition): FormField[] {
    if ( f.type === 'checkselect' ) {
      const fields = (f.options ?? []).map(op => {
        const name = op.value.toString();
        const checkField = this.normalizeFormField({
          name,
          label: op.label || titleCase(name),
          type: 'checkbox'
        }, labelPosition);

        if ( op.hiddenDependent ) {
          const showIf: Record<string, boolean> = {};
          showIf[`${op.value}`] = true
          return [
            checkField,
            this.normalizeFormField(_.extend(op.hiddenDependent, { showIf: showIf }), labelPosition)
          ];
        }

        return checkField;
      });

      return _.flatten(fields);
    }

    if ( f.type === 'daterange' ) {
      return [
        { name: 'fromDate', label: 'From Date', type: 'date', defaultTo: null },
        { name: 'toDate', label: 'From Date', type: 'date', defaultTo: null },
      ].map((s) => this.normalizeFormField(s, labelPosition));

    }

    if ( f.type === 'group' ) {
      return (f.fields ?? []).map((s) => this.normalizeFormField(s, labelPosition));
    }

    if ( f.type === 'row' ) {
      return (f.fields ?? []).map((s) => this.normalizeFormField(s, labelPosition));
    }

    return [];
  }

  private static normalizeSelectOptions(options: SelectOption[]): SelectOption[] {
    return options.map(o => ({
      value: o.value,
      label: o.label || titleCase(o.value.toString()),
      hiddenDependent: o.hiddenDependent,
      tooltip: o.tooltip,
      tooltipPosition: o.tooltipPosition,
    }));
  }

  private static normalizeDependencyExpressions(
    conditionalExpressions: { [name: string]: DependencyValue|DependencyExpression }
  ): FieldDependency[] {
    return _.map(conditionalExpressions, (value, key) => {
      const expr = value as DependencyExpression;
      return <FieldDependency>{
        name: key,
        operator: expr.operator ? expr.operator : 'equalTo',
        value:    expr.operator ? expr.value : value,
        requireVisible: expr.requireVisible || false,
      };
    });
  }

  private static getAngularValidators(field: FormField, controls: { [name: string]: AbstractControl }): ValidatorFn[] {
    const validators: ValidatorFn[] = <ValidatorFn[]>_
      .chain(field.validators)
      .reduce((result, value, key) => {
        result.push(this.buildValidator(field, key as unknown as string, value, controls));
        return result;
      }, [] as (ValidatorFn | undefined)[])
      .compact()
      .value();

    return validators;
  }

  private static buildValidator(
    field: FormField, key: string, value: any, controls: { [name: string]: AbstractControl },
  ): ValidatorFn | undefined {
    const angularValidatorFn = this.getAngularValidator(key, value, controls);
    if (
      field.showIfDependencies
      && field.showIfDependencies.length > 0
    ) {
      return (control: AbstractControl): ValidationErrors | null => {
        return this.dependenciesMet(field, field.showIfDependencies || field.enableIfDependencies || [])
          // angularValidatorFn may be undefined here if `key` doesn't match a known validator; only the
          // showIfDependencies presence is guaranteed by the surrounding branch, not this. Guarding it would
          // silently change behavior (currently throws) for that edge case, so flagging for review instead.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
          ? angularValidatorFn!.call(null, control)
          : null;
      };
    } else if (
      angularValidatorFn
      && field.parentField
      && field.parentField.showIfDependencies
      && field.parentField.showIfDependencies.length > 0
    ) {
      // If we are a subfield and have a validator defined, but no showIf, then we need to check to see if the parent is shown
      // if shown then use our validator, otherwise return null
      const parentField = field.parentField;
      return (control: AbstractControl): ValidationErrors | null => {
        return this.dependenciesMet(parentField, parentField.showIfDependencies || parentField.enableIfDependencies || [])
          ? angularValidatorFn.call(null, control)
          : null;
      };
    } else {
      return angularValidatorFn;
    }
  }

  private static getAngularValidator(key: string, value: any, controls: { [name: string]: AbstractControl }): ValidatorFn | undefined {
    if (key === 'required' && value) { return Validators.required; }
    if (key === 'requiredCheckSelect' && value ) { return validateCheckSelectRequired as ValidatorFn }
    if (key === 'minLength') { return Validators.minLength(value); }
    if (key === 'maxLength') { return Validators.maxLength(value); }
    if (key === 'pattern') { return Validators.pattern(value); }

    if (key === 'min') { return min(value); }
    if (key === 'max') { return max(value); }
    if (key === 'range') { return range(value); }
    if (key === 'digits') { return digits; }
    if (key === 'number') { return number; }
    if (key === 'email') { return email; }
    if (key === 'date') { return date; }
    if (key === 'minDate') { return minDate(value); }
    if (key === 'maxDate') { return maxDate(value); }
    if (key === 'dateISO') { return dateISO; }
    // if (key === 'creditCard') { return creditCard; }
    if (key === 'json') { return json; }
    if (key === 'base64') { return base64; }
    if (key === 'uuid') { return uuid(value); }
    if (key === 'equal') { return equal(value); }
    if (key === 'equalTo') { return equalTo(controls[value]); }
    if (key === 'greaterThanOrEqualTo') { return greaterThanOrEqualTo(controls[value]); }
    if (key === 'lessThanOrEqualTo') { return lessThanOrEqualTo(controls[value]); }
    if (key === 'greaterThanField') { return greaterThanField(controls[value]); }
    if (key === 'lessThanField') { return lessThanField(controls[value]); }

    if (key === 'validateMetaswitchPassword') { return validateMetaswitchPassword as ValidatorFn; }
    if (key === 'validateMetaswitchPIN') { return validateMetaswitchPIN as ValidatorFn; }
    if (key === 'validateMetaswitchVoicemailPIN') { return validateMetaswitchVoicemailPIN as ValidatorFn; }
    if (key === 'ipAddress') { return validateIPAddress as ValidatorFn; }
    if (key === 'usPhoneNumber') { return validateUSPhoneNumber as ValidatorFn; }
    if (key === 'internationalAndUSPhoneNumber') { return validateInternationalAndUSPhoneNumber as ValidatorFn; }
    if (key === 'internationalAndLocalPhoneNumberWithCountry') { return validateInternationalOrLocalPhoneNumberWithCountry(controls[value]) as ValidatorFn }
    if (key === 'internationalAndUSPhoneNumberLenient') { return validateInternationalAndUSPhoneNumberLenient as ValidatorFn; }
    if (key === 'e164WithoutPlus') { return validateE164WithoutPlus as ValidatorFn; }
    if (key === 'url') { return validateUrl as ValidatorFn; }
    if (key === 'subdomain') { return validateSubdomain as ValidatorFn; }
    if (key === 'urlTrailing') { return validateUrlTrailing as ValidatorFn; }
    if (key === 'validRange') { return validRange(value); }
    if (key === 'validateMetaswitchExtension') { return validateMetaswitchExtension as ValidatorFn; }
    if (key === 'validateMetaswitchExtensionNotRequired') { return validateMetaswitchExtensionNotRequired as ValidatorFn; }
    if (key === 'validateMACAddress') { return validateMACAddress as ValidatorFn; }
    if (key === 'validateAlphaNumericWithSpaces') { return validateAlphaNumericWithSpaces as ValidatorFn; }
    if (key === 'validSelection') { return validSelection(value); }
    if (key === 'validSelectionNotRequired') { return validSelectionNotRequired(value); }
    if (key === 'validateEmailAddresses') {return validateEmailAddresses as ValidatorFn; }
    if (key === 'validateEmails') {return validateEmails as ValidatorFn; }
    if (key === 'zipCode') { return zipCode as ValidatorFn; }
    if (key === 'cvv') { return cvv as ValidatorFn; }
    if (key === 'creditCard') { return creditCard as ValidatorFn; }
    if (key === 'addressSimple') { return addressSimple as ValidatorFn; }
    if (key === 'letterRange') { return letterRange(value); }
    if (key === 'time12hour') { return time12hour as ValidatorFn; }
    if (key === 'bandwidthPortTime') { return bandwidthPortTime as ValidatorFn; }
    if (key === 'brightlinkPortTime') { return brightlinkPortTime as ValidatorFn; }
    if (key === 'timeRange') { return timeRange(value); }
    if (key === 'allOrNothing') { return allOrNothing as ValidatorFn; }
    if (key === 'notBlank') { return notBlank as ValidatorFn; }

    return undefined;
  }

  public static validate(form: Form) {
    form.submitted = true;
  }

  public static resetForm(form: Form, input = {}) {
    form.submitted = false;
    form.group.reset(input);
    // Note: in form view, may need to add a directive and call resetForm()
  }

}
